import React, { useEffect, useRef } from "react";
import { Stack, Typography } from "@mui/joy";
import FullPageContainer from "./FullPageContainer";
import { theme } from "styles/theme";
import BackButton from "components/common/BackButton";
import ProgressBar from "components/onboarding/ProgressBar";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import { extractHexColor } from "styles/hexOpacity";
import CloseButtonSmall from "../../components/task/change-status-button/CloseButtonSmall";

export default function FullPageWithHeaderAndProgressV2({
  headerTitle,
  hideHeader,
  hideProgress,
  progress,
  highlightBackground,
  body,
  disableBack,
  onBack,
  showClose,
}: {
  headerTitle?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  progress?: number;
  highlightBackground?: boolean;
  body?: React.ReactNode;
  disableBack?: boolean;
  onBack?: () => void;
  showClose?: boolean;
}) {
  const backgroundColor = !highlightBackground
    ? theme.vars.palette.Background.Level1
    : theme.vars.palette.Background.SoftHighlight;
  const headerHeight = 60; // 헤더 높이
  const progressBarHeight = 12; // 프로그레스바 높이
  const progressPaddingX = 20; // 프로그래스바 양쪽 패딩 값

  const paddingXHeader = 8;
  const paddingXContent = 20;
  const paddingTopContent = hideProgress ? 12 : 32;

  const containerRef = useRef<HTMLDivElement>(null);

  // 화면 잘림 문제: 페이지 최초 진입 시에만 상단으로 스크롤 초기화
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, []);

  const isInappWebview = useIsInappWebview();

  useEffect(() => {
    const backgroundHex = extractHexColor(backgroundColor);

    if (backgroundHex) {
      if (isInappWebview) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              color: `${backgroundHex}`,
            },
          })
        );
      } else {
      }
    }
  }, [backgroundColor, isInappWebview]);

  return (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100vw",
          mx: "auto",
          height: "100vh",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            px: `${paddingXHeader}px`,
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: `${headerHeight}px`, // 헤더 높이 설정
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {!hideHeader && onBack && (
              <BackButton onBack={onBack} disabled={disableBack} />
            )}
            {headerTitle && (
              <Typography
                variant="Body"
                sx={{
                  px: 0,
                  fontColor: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {headerTitle}
              </Typography>
            )}
          </Stack>
          {showClose && <CloseButtonSmall />}
        </Stack>
        {!hideProgress && progress && (
          <Stack
            sx={{
              width: "100%",
              px: `${progressPaddingX}px`,
              mt: 1,
              position: "sticky",
              top: `${headerHeight}px`, // 헤더 높이만큼 아래로 이동
              zIndex: 99,
              height: `${progressBarHeight}px`, // 프로그레스바 높이 설정
            }}
          >
            <ProgressBar progress={progress} />
          </Stack>
        )}
        <Stack
          ref={containerRef}
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            px: `${paddingXContent}px`,
            py: `${paddingTopContent}px`,
            position: "relative",
          }}
        >
          {body}
        </Stack>
      </Stack>
    </FullPageContainer>
  );
}
